import {
  isMonetaryAuthorityQuotesFeatureEnabled,
  isMonetaryAuthoritySettlementFeatureEnabled,
  isOwnWalletSummariesFeatureEnabled,
} from "@/config/runtimeConfig";
import {
  createMonetaryAuthorityOperationId,
  monetaryAuthorityService,
} from "@/services/monetaryAuthorityService";
import { deriveItemPlacementReference } from "@/services/itemPlacementReference";

/**
 * Claps pay "the owner of this item" through the server monetary authority:
 *
 *   quoteItemTransfer({ operationId, fromOrgId, placementRef, amountAtomic })
 *   settleUnitQuote({ operationId, quoteId })
 *
 * The browser supplies intent only: the acting organisation, the item
 * placement reference and an exact atomic debit. The server resolves the item
 * owner and their placement organisation privately, checks the item is
 * visible to the payer, and decides memberships, Wallets, the open/closed
 * economy and the exchange rate. The owner is never named to the browser, and
 * the browser never reads another account's Wallet or an organisation
 * document.
 */

// One clap moves one millionth of the payer's available current balance,
// preserving the legacy clap weight. Integer (floor) division on atomic
// strings; no floating point is involved.
export const CLAP_BALANCE_DIVISOR = 1_000_000n;
export const MAX_CLAPS_PER_BATCH = 150;

const ORG_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/;
const ITEM_ID_PATTERN = /^[a-f0-9]{64}$/;
const PLACEMENT_REF_PATTERN = /^[a-f0-9]{64}$/;
const ATOMIC_PATTERN = /^(0|[1-9]\d*)$/;
const POSITIVE_ATOMIC_PATTERN = /^[1-9]\d*$/;
const MAX_ATOMIC_DIGITS = 60;
const TRANSIENT_CODES = new Set(["unavailable", "deadline-exceeded", "ui-deadline"]);

const CLAP_TRANSFER_MESSAGES = Object.freeze({
  "clap/feature-disabled":
    "Claps are unavailable until secure Unit payments are switched on.",
  "clap/missing-sender": "Sign in before sending a clap.",
  "clap/missing-sender-organisation":
    "Choose an acting organisation before sending claps.",
  "clap/missing-placement": "This item cannot receive claps.",
  "clap/own-content": "You cannot clap your own content.",
  "clap/invalid-amount": "The clap amount is invalid. Please try again.",
  "clap/insufficient-funds":
    "Your acting organisation Wallet does not have enough Units to clap.",
  "clap/economy-closed":
    "This creator's organisation does not accept claps from your acting organisation.",
  "clap/stale-quote":
    "The clap amount changed before it was sent. Nothing was charged. Please clap again.",
  "clap/quote-expired":
    "The clap took too long to confirm. Nothing was charged. Please clap again.",
  "clap/recipient-unavailable": "This creator cannot receive claps right now.",
  "clap/item-unavailable": "This item cannot receive claps right now.",
  "clap/sender-cannot-trade":
    "Your membership in the acting organisation cannot send Units.",
  "clap/wallet-unavailable":
    "Your acting organisation Wallet is not available.",
  "clap/unauthenticated": "Sign in again to send claps.",
  "clap/service-unavailable":
    "Claps are temporarily unavailable. Please try again.",
  "clap/unknown": "Error processing clap. Please try again.",
});

const SERVER_REASON_TO_CLAP_CODE = Object.freeze({
  "feature-disabled": "clap/feature-disabled",
  "insufficient-funds": "clap/insufficient-funds",
  "economy-closed": "clap/economy-closed",
  "stale-quote": "clap/stale-quote",
  "quote-used": "clap/stale-quote",
  "quote-expired": "clap/quote-expired",
  "self-transfer": "clap/own-content",
  "recipient-unavailable": "clap/recipient-unavailable",
  "item-unavailable": "clap/item-unavailable",
  "sender-cannot-trade": "clap/sender-cannot-trade",
  "sender-wallet-unavailable": "clap/wallet-unavailable",
});

const SERVICE_CODE_TO_CLAP_CODE = Object.freeze({
  "feature-disabled": "clap/feature-disabled",
  aborted: "clap/stale-quote",
  unauthenticated: "clap/unauthenticated",
  "stale-auth-context": "clap/unauthenticated",
  unavailable: "clap/service-unavailable",
  "deadline-exceeded": "clap/service-unavailable",
  "ui-deadline": "clap/service-unavailable",
});

export class ClapTransferError extends Error {
  constructor(code) {
    const known = Object.hasOwn(CLAP_TRANSFER_MESSAGES, code) ? code : "clap/unknown";
    super(CLAP_TRANSFER_MESSAGES[known]);
    this.name = "ClapTransferError";
    this.code = known;
  }
}

function clapError(code) {
  return new ClapTransferError(code);
}

/** Maps a clap, monetary-authority or Wallet-summary failure to a clap code. */
export function toClapErrorCode(error) {
  if (error instanceof ClapTransferError) return error.code;
  if (error?.reason && Object.hasOwn(SERVER_REASON_TO_CLAP_CODE, error.reason)) {
    return SERVER_REASON_TO_CLAP_CODE[error.reason];
  }
  const code = String(error?.code || "").split("/").pop();
  if (Object.hasOwn(SERVICE_CODE_TO_CLAP_CODE, code)) {
    return SERVICE_CODE_TO_CLAP_CODE[code];
  }
  return "clap/unknown";
}

export function getClapTransferErrorMessage(error) {
  return CLAP_TRANSFER_MESSAGES[toClapErrorCode(error)];
}

export function isClapTransferFeatureEnabled({
  quotesEnabled = isMonetaryAuthorityQuotesFeatureEnabled,
  settlementEnabled = isMonetaryAuthoritySettlementFeatureEnabled,
  walletSummariesEnabled = isOwnWalletSummariesFeatureEnabled,
} = {}) {
  return (
    quotesEnabled === true &&
    settlementEnabled === true &&
    walletSummariesEnabled === true
  );
}

/**
 * A clap pays the owner of one item placement. Only item-authority cards
 * (`mapAuthorityPlacementToMarketplaceCard`) identify a placement: their
 * canonical `itemId` plus the placement `organisation`, which together derive
 * the server `placementRef`. Legacy, media and other cards return null, so
 * their clap control stays disabled with a reason.
 */
export function resolveClapPlacement(item) {
  const itemId = item?.itemId;
  const orgId = item?.organisation;
  if (
    item?._authorityPresentation !== true ||
    typeof itemId !== "string" ||
    !ITEM_ID_PATTERN.test(itemId) ||
    typeof orgId !== "string" ||
    !ORG_ID_PATTERN.test(orgId) ||
    item.id !== `authority-placement:${orgId}:${itemId}`
  ) {
    return null;
  }
  return Object.freeze({ itemId, orgId });
}

/** Derives the opaque server placement reference for a resolved placement. */
export async function deriveClapPlacementRef(
  placement,
  derive = deriveItemPlacementReference,
) {
  if (!placement) throw clapError("clap/missing-placement");
  try {
    return await derive({ itemId: placement.itemId, orgId: placement.orgId });
  } catch (_error) {
    throw clapError("clap/missing-placement");
  }
}

/**
 * Returns `{ available, code, message }` for a clap control. A control that
 * is unavailable must be rendered disabled with `message` as its reason.
 */
export function getClapAvailability({
  featureEnabled = isClapTransferFeatureEnabled(),
  actorUid,
  fromOrgId,
  placement,
}) {
  let code = null;
  if (!featureEnabled) code = "clap/feature-disabled";
  else if (typeof actorUid !== "string" || !actorUid) code = "clap/missing-sender";
  else if (typeof fromOrgId !== "string" || !ORG_ID_PATTERN.test(fromOrgId)) {
    code = "clap/missing-sender-organisation";
  } else if (!placement) code = "clap/missing-placement";
  return Object.freeze({
    available: code === null,
    code,
    message: code === null ? "" : CLAP_TRANSFER_MESSAGES[code],
  });
}

function requireAtomic(value, pattern = ATOMIC_PATTERN) {
  if (
    typeof value !== "string" ||
    value.length > MAX_ATOMIC_DIGITS ||
    !pattern.test(value)
  ) {
    throw clapError("clap/invalid-amount");
  }
  return value;
}

/** Atomic amount of one clap for a given available current balance. */
export function perClapAtomic(currentAvailableAtomic) {
  return (BigInt(requireAtomic(currentAvailableAtomic)) / CLAP_BALANCE_DIVISOR).toString();
}

/** Exact atomic debit for `count` claps; never uses floating point. */
export function clapBatchAtomic(perClapAmountAtomic, count) {
  if (!Number.isSafeInteger(count) || count < 1 || count > MAX_CLAPS_PER_BATCH) {
    throw clapError("clap/invalid-amount");
  }
  const total = BigInt(requireAtomic(perClapAmountAtomic)) * BigInt(count);
  if (total <= 0n) throw clapError("clap/insufficient-funds");
  return total.toString();
}

export function createClapOperationId(
  createOperationId = createMonetaryAuthorityOperationId,
) {
  return `clap_${createOperationId()}`;
}

/**
 * Freezes one clap batch as a retry-safe transfer intent. The operationId is
 * generated once here and reused for the quote, for the settlement (the
 * server journals them in separate namespaces) and for every retry, so a
 * repeated call replays the same quote/settlement instead of paying twice.
 */
export function createClapTransferIntent({
  fromOrgId,
  placementRef,
  amountAtomic,
  operationId = createClapOperationId(),
}) {
  if (typeof fromOrgId !== "string" || !ORG_ID_PATTERN.test(fromOrgId)) {
    throw clapError("clap/missing-sender-organisation");
  }
  if (typeof placementRef !== "string" || !PLACEMENT_REF_PATTERN.test(placementRef)) {
    throw clapError("clap/missing-placement");
  }
  return Object.freeze({
    operationId,
    fromOrgId,
    placementRef,
    amountAtomic: requireAtomic(amountAtomic, POSITIVE_ATOMIC_PATTERN),
  });
}

function isTransient(error) {
  const code = String(error?.code || "").split("/").pop();
  return TRANSIENT_CODES.has(code) && error?.reason !== "feature-disabled";
}

async function withTransientRetry(run, retries) {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await run();
    } catch (error) {
      if (attempt >= retries || !isTransient(error)) throw error;
    }
  }
}

/**
 * Quotes then settles one frozen clap intent for the acting organisation.
 * `expectedPayeeOrgId` is the placement organisation shown on the card; a
 * quote paying any other organisation is never settled. Resolves with the
 * validated `{ quote, settlement }`; rejects with a ClapTransferError whose
 * message is safe to show.
 */
export async function transferClapMoney(
  intent,
  {
    service = monetaryAuthorityService,
    featureEnabled = isClapTransferFeatureEnabled(),
    expectedPayeeOrgId = null,
    now = () => Date.now(),
    transientRetries = 1,
  } = {},
) {
  if (!featureEnabled) throw clapError("clap/feature-disabled");
  try {
    const quote = await withTransientRetry(
      () => service.quoteItemTransfer(intent),
      transientRetries,
    );
    if (
      quote.kind !== "unit-transfer" ||
      quote.payer.orgId !== intent.fromOrgId ||
      quote.amounts.debitAtomic !== intent.amountAtomic ||
      (expectedPayeeOrgId !== null && quote.payee.orgId !== expectedPayeeOrgId)
    ) {
      throw clapError("clap/unknown");
    }
    if (quote.expiresAt <= now()) throw clapError("clap/quote-expired");
    const settlement = await withTransientRetry(
      () =>
        service.settleUnitQuote({
          operationId: intent.operationId,
          quoteId: quote.quoteId,
        }),
      transientRetries,
    );
    if (
      settlement.kind !== "unit-transfer" ||
      settlement.quoteId !== quote.quoteId ||
      settlement.payerOrgId !== quote.payer.orgId ||
      settlement.payeeOrgId !== quote.payee.orgId ||
      settlement.debitAtomic !== quote.amounts.debitAtomic ||
      settlement.creditAtomic !== quote.amounts.creditAtomic
    ) {
      throw clapError("clap/unknown");
    }
    return Object.freeze({ quote, settlement });
  } catch (error) {
    throw clapError(toClapErrorCode(error));
  }
}
